/**
 * Cookie banner + preferences modal.
 *
 * All the policy lives in consent-manager.js (window.ConsentManager): this file only renders the
 * UI and saves the visitor's choice through ConsentManager.saveChoices(), which calls
 * Shopify.customerPrivacy.setTrackingConsent() so Shopify and app pixels follow it.
 *
 * - The banner shows only in banner regions (EEA, UK, CH, Quebec) until the visitor chooses.
 * - "Reject all" and "Accept all" have the same size and style; "Manage" opens the modal.
 * - Analytics / Marketing / Personalization start unticked until the visitor opts in.
 * - The footer "Cookie preferences" link (any page, any region) reopens the modal.
 * - Text: German, French, Spanish, Italian or Dutch by storefront language, then by the visitor's
 *   country (or browser language in multilingual countries), English otherwise.
 */
(function () {
  'use strict';

  if (!window.ConsentManager || window.customElements.get('cookie-banner')) return;

  var config = window.cookieBannerConfig || {};
  var PRIVACY_URL = config.privacyPolicyUrl || '/policies/privacy-policy';

  var STRINGS = {
    en: {
      text: 'We use cookies to improve your experience and, with your consent, for analytics and marketing. You can accept, reject or choose which cookies to allow.',
      policy: 'Privacy policy',
      manage: 'Manage',
      reject: 'Reject all',
      accept: 'Accept all',
      confirm: 'Confirm choices',
      close: 'Close',
      title: 'Cookie and privacy preferences',
      subtitle: 'You control your data',
      description: 'Learn more about the cookies we use, and choose which cookies to allow.',
      required: 'Required',
      requiredDesc: 'These cookies are necessary for the site to function properly, including capabilities like logging in and adding items to the cart.',
      personalization: 'Personalization',
      personalizationDesc: 'These cookies store details about your actions to personalize your next visit to the website.',
      marketing: 'Marketing',
      marketingDesc: 'These cookies are used by us and our partners, including Shopify, to optimize marketing communications and show you ads on other websites.',
      analytics: 'Analytics',
      analyticsDesc: 'These cookies help us understand how you interact with the site. We use this data to identify areas to improve.',
    },
    de: {
      text: 'Wir verwenden Cookies, um Ihr Erlebnis zu verbessern, und mit Ihrer Einwilligung für Analyse- und Marketingzwecke. Sie können alle akzeptieren, alle ablehnen oder auswählen, welche Cookies Sie zulassen.',
      policy: 'Datenschutzerklärung',
      manage: 'Verwalten',
      reject: 'Alle ablehnen',
      accept: 'Alle akzeptieren',
      confirm: 'Auswahl bestätigen',
      close: 'Schließen',
      title: 'Cookie- und Datenschutzeinstellungen',
      subtitle: 'Sie entscheiden über Ihre Daten',
      description: 'Erfahren Sie mehr über die von uns verwendeten Cookies und wählen Sie, welche Cookies Sie zulassen möchten.',
      required: 'Erforderlich',
      requiredDesc: 'Diese Cookies sind für die ordnungsgemäße Funktion der Website erforderlich, z. B. für die Anmeldung und das Hinzufügen von Artikeln zum Warenkorb.',
      personalization: 'Personalisierung',
      personalizationDesc: 'Diese Cookies speichern Informationen über Ihre Aktionen, um Ihren nächsten Besuch auf der Website zu personalisieren.',
      marketing: 'Marketing',
      marketingDesc: 'Diese Cookies werden von uns und unseren Partnern, einschließlich Shopify, verwendet, um Marketingkommunikation zu optimieren und Ihnen Werbung auf anderen Websites anzuzeigen.',
      analytics: 'Analyse',
      analyticsDesc: 'Diese Cookies helfen uns zu verstehen, wie Sie mit der Website interagieren. Wir nutzen diese Daten, um Verbesserungsmöglichkeiten zu erkennen.',
    },
    fr: {
      text: 'Nous utilisons des cookies pour améliorer votre expérience et, avec votre consentement, à des fins d’analyse et de marketing. Vous pouvez tout accepter, tout refuser ou choisir les cookies que vous autorisez.',
      policy: 'Politique de confidentialité',
      manage: 'Gérer',
      reject: 'Tout refuser',
      accept: 'Tout accepter',
      confirm: 'Confirmer mes choix',
      close: 'Fermer',
      title: 'Préférences en matière de cookies et de confidentialité',
      subtitle: 'Vous contrôlez vos données',
      description: 'Découvrez les cookies que nous utilisons et choisissez ceux que vous autorisez.',
      required: 'Nécessaires',
      requiredDesc: 'Ces cookies sont indispensables au bon fonctionnement du site, notamment pour la connexion et l’ajout d’articles au panier.',
      personalization: 'Personnalisation',
      personalizationDesc: 'Ces cookies enregistrent des informations sur vos actions afin de personnaliser votre prochaine visite sur le site.',
      marketing: 'Marketing',
      marketingDesc: 'Ces cookies sont utilisés par nous et nos partenaires, dont Shopify, pour optimiser nos communications marketing et vous montrer des publicités sur d’autres sites.',
      analytics: 'Statistiques',
      analyticsDesc: 'Ces cookies nous aident à comprendre comment vous utilisez le site. Nous utilisons ces données pour identifier les points à améliorer.',
    },
    es: {
      text: 'Utilizamos cookies para mejorar tu experiencia y, con tu consentimiento, con fines analíticos y de marketing. Puedes aceptarlas todas, rechazarlas todas o elegir cuáles permitir.',
      policy: 'Política de privacidad',
      manage: 'Gestionar',
      reject: 'Rechazar todo',
      accept: 'Aceptar todo',
      confirm: 'Confirmar selección',
      close: 'Cerrar',
      title: 'Preferencias de cookies y privacidad',
      subtitle: 'Tú controlas tus datos',
      description: 'Descubre más sobre las cookies que utilizamos y elige cuáles quieres permitir.',
      required: 'Necesarias',
      requiredDesc: 'Estas cookies son necesarias para que el sitio funcione correctamente, por ejemplo para iniciar sesión y añadir artículos al carrito.',
      personalization: 'Personalización',
      personalizationDesc: 'Estas cookies guardan información sobre tus acciones para personalizar tu próxima visita al sitio.',
      marketing: 'Marketing',
      marketingDesc: 'Estas cookies las utilizamos nosotros y nuestros socios, incluido Shopify, para optimizar las comunicaciones de marketing y mostrarte anuncios en otros sitios web.',
      analytics: 'Analíticas',
      analyticsDesc: 'Estas cookies nos ayudan a entender cómo interactúas con el sitio. Utilizamos estos datos para identificar aspectos que mejorar.',
    },
    it: {
      text: 'Utilizziamo i cookie per migliorare la tua esperienza e, con il tuo consenso, per finalità di analisi e marketing. Puoi accettarli tutti, rifiutarli tutti o scegliere quali consentire.',
      policy: 'Informativa sulla privacy',
      manage: 'Gestisci',
      reject: 'Rifiuta tutto',
      accept: 'Accetta tutto',
      confirm: 'Conferma le scelte',
      close: 'Chiudi',
      title: 'Preferenze su cookie e privacy',
      subtitle: 'Hai il controllo dei tuoi dati',
      description: 'Scopri di più sui cookie che utilizziamo e scegli quali consentire.',
      required: 'Necessari',
      requiredDesc: 'Questi cookie sono necessari per il corretto funzionamento del sito, ad esempio per l’accesso e l’aggiunta di articoli al carrello.',
      personalization: 'Personalizzazione',
      personalizationDesc: 'Questi cookie memorizzano informazioni sulle tue azioni per personalizzare la tua prossima visita al sito.',
      marketing: 'Marketing',
      marketingDesc: 'Questi cookie sono utilizzati da noi e dai nostri partner, incluso Shopify, per ottimizzare le comunicazioni di marketing e mostrarti annunci su altri siti web.',
      analytics: 'Statistiche',
      analyticsDesc: 'Questi cookie ci aiutano a capire come interagisci con il sito. Utilizziamo questi dati per individuare gli aspetti da migliorare.',
    },
    nl: {
      text: 'We gebruiken cookies om je ervaring te verbeteren en, met je toestemming, voor analyse en marketing. Je kunt alles accepteren, alles weigeren of kiezen welke cookies je toestaat.',
      policy: 'Privacybeleid',
      manage: 'Beheren',
      reject: 'Alles weigeren',
      accept: 'Alles accepteren',
      confirm: 'Keuzes bevestigen',
      close: 'Sluiten',
      title: 'Cookie- en privacyvoorkeuren',
      subtitle: 'Jij bepaalt wat er met je gegevens gebeurt',
      description: 'Lees meer over de cookies die we gebruiken en kies welke cookies je toestaat.',
      required: 'Noodzakelijk',
      requiredDesc: 'Deze cookies zijn nodig om de site goed te laten werken, bijvoorbeeld om in te loggen en artikelen aan je winkelwagen toe te voegen.',
      personalization: 'Personalisatie',
      personalizationDesc: 'Deze cookies slaan gegevens over je acties op om je volgende bezoek aan de website te personaliseren.',
      marketing: 'Marketing',
      marketingDesc: 'Deze cookies worden door ons en onze partners, waaronder Shopify, gebruikt om marketingcommunicatie te optimaliseren en je advertenties op andere websites te tonen.',
      analytics: 'Analyse',
      analyticsDesc: 'Deze cookies helpen ons te begrijpen hoe je de site gebruikt. We gebruiken deze gegevens om te bepalen wat we kunnen verbeteren.',
    },
  };

  // Countries with one clear banner language. Multilingual ones (BE, CH, LU) use the browser language.
  var COUNTRY_LANGUAGE = {
    DE: 'de', AT: 'de', LI: 'de',
    FR: 'fr', MC: 'fr',
    ES: 'es',
    IT: 'it', SM: 'it', VA: 'it',
    NL: 'nl',
  };
  var MULTILINGUAL = ['BE', 'CH', 'LU', 'CA'];

  function pickLanguage(region) {
    var supported = function (code) {
      code = String(code || '').slice(0, 2).toLowerCase();
      return STRINGS[code] ? code : null;
    };
    var storefront = supported(config.locale || document.documentElement.lang);
    if (storefront && storefront !== 'en') return storefront;

    region = String(region || '').toUpperCase();
    if (region.slice(0, 4) === 'CAQC') return 'fr';
    var country = region.slice(0, 2);
    if (COUNTRY_LANGUAGE[country]) return COUNTRY_LANGUAGE[country];
    if (MULTILINGUAL.indexOf(country) !== -1) {
      var browser = (navigator.languages && navigator.languages[0]) || navigator.language;
      return supported(browser) || 'en';
    }
    return 'en';
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var CATEGORIES = ['personalization', 'marketing', 'analytics']; // UI name -> consent key below
  var CONSENT_KEY = { personalization: 'preferences', marketing: 'marketing', analytics: 'analytics' };

  class CookieBanner extends HTMLElement {
    connectedCallback() {
      if (this._rendered) return;
      this._rendered = true;

      var state = window.ConsentManager.getState();
      this.t = STRINGS[pickLanguage(state.region)];
      // Opt-in regions without a choice yet: everything unticked. Otherwise reflect the current state.
      this.choices = {
        personalization: state.consentRequired && !state.hasChosen ? false : state.preferences,
        marketing: state.consentRequired && !state.hasChosen ? false : state.marketing,
        analytics: state.consentRequired && !state.hasChosen ? false : state.analytics,
      };

      this.innerHTML = this.template();
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.syncCheckboxes();
    }

    template() {
      var t = this.t;
      var esc = escapeHtml;
      var category = function (key, checked, required) {
        return (
          '<div class="cookie-category cookie-category--' + key + '">' +
          '<div class="cookie-category__header">' +
          '<h4 class="cookie-category__title" id="cookie-category-' + key + '">' + esc(t[key]) + '</h4>' +
          '<button type="button" role="checkbox" class="cookie-category__checkbox' + (required ? ' required' : '') + '"' +
          ' data-category="' + key + '" aria-labelledby="cookie-category-' + key + '"' +
          ' aria-checked="' + (checked ? 'true' : 'false') + '"' + (required ? ' aria-disabled="true"' : '') + '></button>' +
          '</div>' +
          '<p class="cookie-category__description">' + esc(t[key + 'Desc']) + '</p>' +
          '</div>'
        );
      };

      return (
        '<div class="cookie-banner" role="region" aria-label="' + esc(t.title) + '">' +
        '<div class="cookie-banner__content">' +
        '<p class="cookie-banner__text">' + esc(t.text) + ' ' +
        '<a href="' + esc(PRIVACY_URL) + '" class="cookie-banner__link" target="_blank" rel="noopener">' + esc(t.policy) + '</a></p>' +
        '</div>' +
        '<div class="cookie-banner__buttons">' +
        '<button type="button" class="cookie-banner__btn cookie-banner__btn--choice cookie-banner__btn--reject" data-action="reject">' + esc(t.reject) + '</button>' +
        '<button type="button" class="cookie-banner__btn cookie-banner__btn--choice cookie-banner__btn--accept" data-action="accept">' + esc(t.accept) + '</button>' +
        '</div>' +
        '<button type="button" class="cookie-banner__btn cookie-banner__btn--manage" data-action="manage">' + esc(t.manage) + '</button>' +
        '</div>' +
        '<div class="cookie-modal" data-action="backdrop">' +
        '<div class="cookie-modal__content" role="dialog" aria-modal="true" aria-labelledby="cookie-modal-title">' +
        '<div class="cookie-modal__header">' +
        '<h2 class="cookie-modal__title" id="cookie-modal-title">' + esc(t.title) + '</h2>' +
        '<h3 class="cookie-modal__subtitle">' + esc(t.subtitle) + '</h3>' +
        '<p class="cookie-modal__description">' + esc(t.description) + ' ' +
        '<a href="' + esc(PRIVACY_URL) + '" class="cookie-modal__link" target="_blank" rel="noopener">' + esc(t.policy) + '</a></p>' +
        '<button type="button" class="cookie-modal__close" data-action="close" aria-label="' + esc(t.close) + '">' +
        '<svg class="icon-close" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="M6 6L18 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '<path d="M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        '</button>' +
        '</div>' +
        '<div class="cookie-modal__body">' +
        category('required', true, true) +
        category('personalization', this.choices.personalization) +
        category('marketing', this.choices.marketing) +
        category('analytics', this.choices.analytics) +
        '</div>' +
        '<div class="cookie-modal__footer">' +
        '<button type="button" class="cookie-modal__btn cookie-modal__btn--confirm" data-action="confirm">' + esc(t.confirm) + '</button>' +
        '<button type="button" class="cookie-modal__btn cookie-modal__btn--choice cookie-modal__btn--reject" data-action="reject">' + esc(t.reject) + '</button>' +
        '<button type="button" class="cookie-modal__btn cookie-modal__btn--choice cookie-modal__btn--accept" data-action="accept">' + esc(t.accept) + '</button>' +
        '</div>' +
        '</div>' +
        '</div>'
      );
    }

    onClick(e) {
      var target = e.target.closest('[data-action], [data-category]');
      if (!target || !this.contains(target)) return;

      if (target.hasAttribute('data-category')) {
        this.toggle(target.getAttribute('data-category'));
        return;
      }
      var action = target.getAttribute('data-action');
      if (action === 'backdrop' && e.target !== target) return; // click inside the dialog
      if (action === 'manage') this.showModal();
      if (action === 'close' || action === 'backdrop') this.hideModal();
      if (action === 'accept') this.save({ personalization: true, marketing: true, analytics: true });
      if (action === 'reject') this.save({ personalization: false, marketing: false, analytics: false });
      if (action === 'confirm') this.save(this.choices);
    }

    onKeydown(e) {
      if (e.key === 'Escape') this.hideModal();
    }

    toggle(category) {
      if (!CONSENT_KEY[category]) return; // "required" can't be turned off
      this.choices[category] = !this.choices[category];
      this.syncCheckboxes();
    }

    syncCheckboxes() {
      var self = this;
      CATEGORIES.forEach(function (category) {
        var box = self.querySelector('[data-category="' + category + '"]');
        if (!box) return;
        box.classList.toggle('checked', !!self.choices[category]);
        box.setAttribute('aria-checked', self.choices[category] ? 'true' : 'false');
      });
    }

    save(choices) {
      this.choices = {
        personalization: !!choices.personalization,
        marketing: !!choices.marketing,
        analytics: !!choices.analytics,
      };
      this.syncCheckboxes();
      window.ConsentManager.saveChoices({
        analytics: this.choices.analytics,
        marketing: this.choices.marketing,
        preferences: this.choices.personalization,
      });
      this.hideBanner();
      this.hideModal();
    }

    showBanner() {
      this.querySelector('.cookie-banner').classList.add('show');
    }

    hideBanner() {
      this.querySelector('.cookie-banner').classList.remove('show');
    }

    showModal() {
      this._lastFocus = document.activeElement;
      this.querySelector('.cookie-modal').classList.add('show');
      document.body.style.overflow = 'hidden';
      var close = this.querySelector('.cookie-modal__close');
      if (close) close.focus();
    }

    hideModal() {
      var modal = this.querySelector('.cookie-modal');
      if (!modal.classList.contains('show')) return;
      modal.classList.remove('show');
      document.body.style.overflow = '';
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }
  }

  customElements.define('cookie-banner', CookieBanner);

  function getBanner() {
    var el = document.querySelector('cookie-banner');
    if (!el) {
      el = document.createElement('cookie-banner');
      document.body.appendChild(el);
    }
    return el;
  }

  // Show the banner in opt-in regions until the visitor chooses.
  window.ConsentManager.ready(function (state) {
    if (state.consentRequired && !state.hasChosen) getBanner().showBanner();
  });

  // Footer "Cookie preferences" link (and anything else calling ConsentManager.openPreferences()).
  function openPreferences() {
    window.ConsentManager.ready(function () {
      getBanner().showModal();
    });
  }
  document.addEventListener('consent:open-preferences', openPreferences);
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-cookie-preferences]');
    if (!link) return;
    e.preventDefault();
    openPreferences();
  });
  if (window.location.hash === '#cookie-preferences') openPreferences();
})();
